import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import { Alert, Box, Button, CircularProgress, DataGrid, Typography } from 'components/AORedesign';
import { currencyStringFormatter } from 'helpers';
import BackToPaymentManagementButton from '../BackToPaymentManagementButton';
import {
  handleClickEnterPayments as _handleClickEnterPayments,
  handleClickPaymentList as _handleClickPaymentList,
  handleClickPaymentValidation as _handleClickPaymentValidation,
  handleClickSubmitPostPayment as _handleClickSubmitPostPayment,
  handleOnCellEditCommit as _handleOnCellEditCommit,
  onSelectionModelChange as _onSelectionModelChange,
  enterPaymentCol,
  fetchPaymentAccountsList,
  paymentAccCol,
  rowInit,
} from './PostPayment.api';
import PostPaymentConfirmation from './PostPaymentConfirmation';
import PostPaymentValidation from './PostPaymentValidation';
import PostPaymentValidationTable from './PostPaymentValidationTable';

const screenMappings = {
  start: `BillingServicesPaymentManagementPostPaymentPanel`,
  enterPayments: `BillingServicesPaymentManagementPostPaymentPanelEnterPayments`,
  paymentValidation: `BillingServicesPaymentManagementPostPaymentPanelPaymentValidation`,
  paymentConfirmation: `BillingServicesPaymentManagementPostPaymentPanelPaymentConfirmation`,
};

const BillingServicesPaymentManagementPostPayment = ({ resetFlow }) => {
  const [currentScreen, setCurrentScreen] = useState(screenMappings.start);
  const [postPaymentData, setPostPaymentData] = useState({
    paymentAccountsList: [rowInit],
    paymentAccountsListToEdit: [rowInit],
    paymentAccountsListLoading: true,
    alert: {
      isVisible: false,
      message: null,
      severity: null,
    },
    enterPaymentTotal: null,
    enterPaymentContinueDisabled: true,
  });
  const [selectionModel, setSelectionModel] = useState([]);

  useEffect(() => {
    fetchPaymentAccountsList(setPostPaymentData);
  }, []);

  const paymentAccList = postPaymentData.paymentAccountsList;
  const paymentAccListLoading = postPaymentData.paymentAccountsListLoading;
  const alertIsVisible = postPaymentData.alert.isVisible;
  const alertMessage = postPaymentData.alert.message;
  const alertSeverity = postPaymentData.alert.severity;
  const enterPaymentList = postPaymentData.paymentAccountsListToEdit;
  const enterPaymentTotal = postPaymentData.enterPaymentTotal;
  const enterPaymentContinueDisabled = postPaymentData.enterPaymentContinueDisabled;

  const handleClickBackToPaymentManagement = () => resetFlow(); // function from parent to reset flow

  const onSelectionModelChange = (selection) => _onSelectionModelChange(selection, postPaymentData, setSelectionModel, setPostPaymentData);

  const handleClickPaymentList = (e) => _handleClickPaymentList(e, screenMappings, setCurrentScreen, setPostPaymentData);

  const handleClickEnterPayments = () => _handleClickEnterPayments(screenMappings, setCurrentScreen, postPaymentData, setPostPaymentData);

  const handleOnCellEditCommit = (params) => _handleOnCellEditCommit(params, postPaymentData, setPostPaymentData);

  const handleClickPaymentValidation = (e) => _handleClickPaymentValidation(e, screenMappings, setCurrentScreen, setPostPaymentData);

  const handleClickSubmitPostPayment = () => _handleClickSubmitPostPayment(screenMappings, setCurrentScreen, postPaymentData, setPostPaymentData);

  return (
    <>
      {currentScreen === screenMappings.start && (
        <>
          <BackToPaymentManagementButton handleOnClick={handleClickBackToPaymentManagement} />

          <Box mb={2}>
            <Typography variant="h5">Payment Management</Typography>
          </Box>

          <Box mb={2}>
            <Typography>Select Accounts For Payment</Typography>
          </Box>

          {alertIsVisible && <Alert severity={alertSeverity}>{alertMessage}</Alert>}

          <Box my={2}>
            {paymentAccListLoading ? (
              <CircularProgress />
            ) : (
              <DataGrid
                autoHeight
                pageSize={10}
                checkboxSelection
                rows={paymentAccList}
                columns={paymentAccCol}
                selectionModel={selectionModel}
                onSelectionModelChange={onSelectionModelChange}
              />
            )}
          </Box>

          <Box display="flex" justifyContent="flex-end">
            <Button onClick={handleClickEnterPayments}>Select</Button>
          </Box>
        </>
      )}

      {currentScreen === screenMappings.enterPayments && (
        <>
          <BackToPaymentManagementButton handleOnClick={handleClickBackToPaymentManagement} />

          <Box mb={2}>
            <Typography variant="h5">Payment Management</Typography>
          </Box>

          <Box my={2}>
            <Typography>Enter Payments</Typography>
          </Box>

          {alertIsVisible && <Alert severity={alertSeverity}>{alertMessage}</Alert>}

          <Box my={2}>
            <DataGrid autoHeight pageSize={10} rows={enterPaymentList} columns={enterPaymentCol} onCellEditCommit={handleOnCellEditCommit} />
          </Box>

          <Box mb={4}>
            <Box display="flex" justifyContent={'flex-end'}>
              <Box mr={2}>
                <Typography fontWeight="bold">Payment Amount Total:</Typography>
              </Box>
              <Typography display="flex" alignItems="center">
                {currencyStringFormatter(enterPaymentTotal)}
              </Typography>
            </Box>
          </Box>

          <Box display="flex" justifyContent="flex-end">
            <Button variant="outlined" onClick={handleClickPaymentList} style={{ marginRight: 8 }}>
              Previous
            </Button>
            <Button disabled={enterPaymentContinueDisabled} onClick={handleClickPaymentValidation}>
              Continue
            </Button>
          </Box>
        </>
      )}

      {currentScreen === screenMappings.paymentValidation && (
        <>
          <BackToPaymentManagementButton handleOnClick={handleClickBackToPaymentManagement} />

          <Box mb={2}>
            <Typography variant="h5">Payment Management</Typography>
          </Box>

          <Box my={2}>
            <Typography>Payment Validation</Typography>
          </Box>

          <PostPaymentValidation tableRows={enterPaymentList} paymentTotal={enterPaymentTotal} />

          {alertIsVisible && <Alert severity={alertSeverity}>{alertMessage}</Alert>}

          <PostPaymentValidationTable tableRows={enterPaymentList} />

          <Box display="flex" justifyContent="flex-end" mt={2}>
            <Button variant="outlined" onClick={handleClickEnterPayments} style={{ marginRight: 8 }}>
              Previous
            </Button>
            <Button onClick={handleClickSubmitPostPayment}>Submit</Button>
          </Box>
        </>
      )}

      {currentScreen === screenMappings.paymentConfirmation && (
        <>
          <BackToPaymentManagementButton handleOnClick={handleClickBackToPaymentManagement} />

          <Box mb={2}>
            <Typography variant="h5">Payment Management</Typography>
          </Box>

          <Box my={2}>
            <Typography>Payment Confirmation</Typography>
          </Box>

          <PostPaymentConfirmation tableRows={enterPaymentList} paymentTotal={enterPaymentTotal} />

          <Box display="flex" justifyContent="center" mt={2}>
            <Button variant="text" color="secondary" onClick={handleClickPaymentList}>
              Go to Post Payment
            </Button>
          </Box>
        </>
      )}
    </>
  );
};

BillingServicesPaymentManagementPostPayment.propTypes = {
  resetFlow: PropTypes.func,
};

export default BillingServicesPaymentManagementPostPayment;
